import { z } from 'zod'

import { LocalizedSchema } from './common'
import { ORDER_STATUSES, type OrderStatus, OrderStatusSchema } from './shop-orders'

// Online store, phase 3: the sales report and the low-stock list (CMS only). Revenue counts paid orders only; days
// are Jakarta days (WIB, UTC+7).

/** Orders that were paid and not refunded or cancelled since: the revenue. */
export const PAID_STATUSES: readonly OrderStatus[] = ['paid', 'packed', 'shipped', 'delivered']

export const REPORT_RANGES = [7, 30, 90] as const
export const ReportQuerySchema = z.object({ days: z.coerce.number().int().pipe(z.union([z.literal(7), z.literal(30), z.literal(90)])).default(30) })

export const SalesDaySchema = z.object({ date: z.string(), orders: z.number().int(), paidOrders: z.number().int(), revenue: z.number().int() })
export type SalesDay = z.infer<typeof SalesDaySchema>

export const TopProductSchema = z.object({ productId: z.string(), title: z.string(), path: z.string(), qty: z.number().int(), revenue: z.number().int() })

export const SalesReportSchema = z.object({
  days: z.number().int(),
  /** First and last day of the range (Jakarta), inclusive. */
  from: z.string(),
  to: z.string(),
  /** One row per day, oldest first, days without orders included. */
  daily: z.array(SalesDaySchema),
  byStatus: z.record(OrderStatusSchema, z.number().int()),
  topProducts: z.array(TopProductSchema),
  totals: z.object({
    orders: z.number().int(),
    paidOrders: z.number().int(),
    expiredOrders: z.number().int(),
    revenue: z.number().int(),
    /** Revenue ÷ paid orders, rounded; 0 without paid orders. */
    averageOrder: z.number().int(),
    /** Paid ÷ (paid + expired), 0–1; null when neither happened. */
    paidRate: z.number().nullable(),
    /** Discount codes' rupiah on paid orders. */
    discount: z.number().int(),
  }),
  /** Variants at or under their low-stock mark. */
  lowStock: z.number().int(),
})
export type SalesReport = z.infer<typeof SalesReportSchema>

export const emptyByStatus = (): Record<OrderStatus, number> => Object.fromEntries(ORDER_STATUSES.map((s) => [s, 0])) as Record<OrderStatus, number>

export const LowStockItemSchema = z.object({
  productId: z.string(),
  title: LocalizedSchema,
  status: z.string(),
  sku: z.string(),
  color: z.string(),
  size: z.string(),
  stock: z.number().int(),
  lowStock: z.number().int(),
})
export type LowStockItem = z.infer<typeof LowStockItemSchema>
export const LowStockListSchema = z.object({ items: z.array(LowStockItemSchema), total: z.number().int() })
